import { hasValue } from '@hprofits/utils/fp/optional';
import type { Ref } from 'vue';
import { computed, onScopeDispose, readonly, ref } from 'vue';
import { useRouter } from 'vue-router';

import { useLegacyApi } from '@/composables/api/useLegacyCreativeApi';
import { useIsAdmin } from '@/composables/useIsAdmin';
import { useCampaignGroupContextActions } from '@/modules/campaigns/composables/actions/useCampaignGroupContextActions';
import { provideCampaignGroupContext } from '@/modules/campaigns/composables/context/campaignGroupContextProvider';
import type { DataDispatcher } from '@/modules/common/state/types';
import { usePageController } from '@/modules/common/state/usePageController';

import {
  CampaignGroupActions,
  CampaignGroupContext,
  CampaignGroupParams,
  CampaignGroupState,
  CampaignRightPanelController,
  DiscardChanges,
} from '../context/types';
import { useAdUnitTypesDataSource } from '../state/useAdUnitTypesDataSource';
import { useCampaignGroupDataSource } from '../state/useCampaignGroupDataSource';
import { useCampaignStatisticsDataSource } from '../state/useCampaignStatisticsDataSource';
import { useCampaignOwnerInfo } from '../useCampaignOwnerInfo';
import { useOwnerGrants } from '../useOwnerGrants';

// Used to subscribe external dispatcher sources for dirty state tracking
type SubmitController = Readonly<{
  canSubmit: Readonly<Ref<boolean>>;
  isPending: Readonly<Ref<boolean>>;
  isDirty: Readonly<Ref<boolean>>;
  submit: () => Promise<boolean>;
  discardChanges?: DiscardChanges;
}>;

export const createCampaignRightPanelController = (): CampaignRightPanelController => {
  const isRegistered = ref(false);
  const isDesktopAvailable = ref(false);
  const isOpen = ref(false);
  const isAvailable = computed(() => isRegistered.value && isDesktopAvailable.value);

  const close = (): void => {
    isOpen.value = false;
  };

  const register = (): void => {
    isRegistered.value = true;
  };

  const unregister = (): void => {
    isRegistered.value = false;
    close();
  };

  const setDesktopAvailable = (available: boolean): void => {
    isDesktopAvailable.value = available;
    if (!available) {
      close();
    }
  };

  const toggle = (): void => {
    if (isAvailable.value) {
      isOpen.value = !isOpen.value;
    }
  };

  return {
    isAvailable,
    isOpen: readonly(isOpen),
    register,
    unregister,
    setDesktopAvailable,
    toggle,
    close,
  };
};

export const useCampaignGroupContextProvider = (params: CampaignGroupParams): CampaignGroupContext => {
  const pageController = usePageController();

  // Unsaved-changes navigation guard (handled in CampaignWrapper): a delete or
  // other programmatic navigation calls setSkipNextNavigationGuard so the guard
  // lets the next navigation through; router.afterEach resets the one-shot flag.
  const router = useRouter();
  const skipNext = ref(false);
  const setSkipNextNavigationGuard = (): void => {
    skipNext.value = true;
  };
  onScopeDispose(
    router.afterEach(() => {
      skipNext.value = false;
    }),
  );

  const adUnitTypesSource = useAdUnitTypesDataSource('flat');
  const campaignGroupSource = useCampaignGroupDataSource(params);
  const campaignGroupStatisticsSource = useCampaignStatisticsDataSource(params);

  const campaignGroupActions = useCampaignGroupContextActions({
    params,
    campaignGroupSource,
    campaignGroupStatisticsSource,
    pageController,
    setSkipNextNavigationGuard,
  });
  const subscribedDispatcher = ref<SubmitController | null>(null);

  const hasLoading = computed<boolean>(
    () => campaignGroupSource.state.isLoading.value || campaignGroupStatisticsSource.state.isLoading.value,
  );
  const canSubmit = computed<boolean>(() => subscribedDispatcher.value?.canSubmit ?? false);
  const isPending = computed<boolean>(() => subscribedDispatcher.value?.isPending ?? false);
  const isDirty = computed<boolean>(() => subscribedDispatcher.value?.isDirty ?? false);
  const hasLoadingOrPending = computed<boolean>(
    () => hasLoading.value || isPending.value || campaignGroupActions.isPending.value,
  );

  const submit = async (): Promise<boolean> => {
    const submitFn = subscribedDispatcher.value?.submit;
    return hasValue(submitFn) ? submitFn() : false;
  };

  const discardChanges = (): void => {
    subscribedDispatcher.value?.discardChanges?.();
    pageController.errors.clear();
  };

  const { ownerId: campaignGroupOwnerId } = useCampaignOwnerInfo(campaignGroupSource.state.data);

  const state: CampaignGroupState = {
    hasLoading,
    hasLoadingOrPending,
    canSubmit,
    isDirty,
    campaignGroupOwnerId,
  };

  const actions: CampaignGroupActions = {
    deleteCampaignGroup: campaignGroupActions.deleteCampaignGroup,
    createTargetGroup: campaignGroupActions.createTargetGroup,
    copyTargetGroup: campaignGroupActions.copyTargetGroup,
    createCreativeGroup: campaignGroupActions.createCreativeGroup,
    createAdGroup: campaignGroupActions.createAdGroup,
    copyAdGroup: campaignGroupActions.copyAdGroup,
    deleteTargetGroup: campaignGroupActions.deleteTargetGroup,
    deleteCreativeGroup: campaignGroupActions.deleteCreativeGroup,
    deleteAdGroup: campaignGroupActions.deleteAdGroup,
    reload: campaignGroupSource.actions.reload,
    submit,
    discardChanges,
  };

  const subscribe = (dispatcher: DataDispatcher, discard?: DiscardChanges) => {
    subscribedDispatcher.value = {
      canSubmit: computed(() => {
        const isDirty = dispatcher.state.isDirty.value;
        const isPending = dispatcher.state.isPending.value;
        const isLoading = campaignGroupSource.state.isLoading.value;
        return isDirty && !isLoading && !isPending;
      }),
      isPending: computed(() => dispatcher.state.isPending.value),
      isDirty: computed(() => dispatcher.state.isDirty.value),
      submit: dispatcher.actions.submit,
      discardChanges: discard,
    };
  };

  const unsubscribe = () => {
    subscribedDispatcher.value = null;
  };

  const isAdmin = useIsAdmin();
  const { auth: legacyAuthApi } = useLegacyApi();

  const { hasEffectiveGrant } = useOwnerGrants({
    isAdmin,
    campaignGroupOwnerId: campaignGroupOwnerId,
    authApi: legacyAuthApi,
  });

  const context: CampaignGroupContext = {
    params,
    page: pageController,
    adUnitTypesSource,
    campaignGroupSource,
    campaignGroupStatisticsSource,
    state,
    actions,
    hooks: {
      subscribe,
      unsubscribe,
    },
    acl: {
      isAdmin,
      hasEffectiveGrant,
    },
    skipNextNavigationGuard: skipNext,
    setSkipNextNavigationGuard,
    creativeCreation: campaignGroupActions.creativeCreationState,
    isCreatingTargetGroup: campaignGroupActions.isCreatingTargetGroup,
    copyingTargetGroupId: campaignGroupActions.copyingTargetGroupId,
    copyingAdGroupId: campaignGroupActions.copyingAdGroupId,
    entityDeletion: campaignGroupActions.entityDeletion,
    rightPanel: createCampaignRightPanelController(),
  };

  provideCampaignGroupContext(context);
  return context;
};
